<template>
  <div class="wt-transcribe-modal-inner wt2-modal-inner">
    <div class="-header">
      <div class="-title wt2-text-l2">
        {{ 'transcribe.longTitle' | culture }}
      </div>

      <div v-if="isInLightbox || showHeaderStatus" class="-sep-line" />

      <div
        v-if="showSegmentTitle"
        class="-rec-name wt2-text-n"
        v-text="activeSegment.title"
      />
      <div v-else class="-spring" />

      <div v-if="showHeaderStatus" class="-status wt2-text-n">
        Transcript in progress<wt-ellipsis-animation />
      </div>
    </div>

    <div class="-top" ref="scrollArea" @scroll.passive="onScroll">
      <div class="-keep-token-editor-open">
        <i
          class="wt2-close wticon wticon-close close-icon wt2-color-normal wt-cursor-pointer"
          @click="close"
        />
      </div>

      <template v-if="!list.length">
        <div class="-transcription-state">
          You have no transcript on this video
        </div>
      </template>

      <template v-if="failedToCreate">
        <div class="-transcription-state">
          Transcript of this video did not succeed. You can close and try again
          later
        </div>
      </template>

      <template v-if="!isUserOwnTranscript">
        <div v-if="showTranscribeVideo" class="-transcription-state">
          {{ 'transcribe.transcribeThisVideoTxt' | culture }}
          <wt-button
            tabindex="0"
            type="hollow-dark"
            :text="'transcribe.transcribeThisVideoBtn' | culture"
            @click.native="transcribeVideo"
          />
        </div>

        <div
          v-else-if="showFailedState"
          class="-transcription-state -failed-state"
        >
          {{ 'transcribe.somethingWentWrong' | culture }}
          <wt-button
            tabindex="0"
            type="hollow-dark"
            :text="'transcribe.insertYourOwn' | culture"
            @click.native="writeYourOwn"
          />
        </div>

        <div v-else-if="showInProgressState" class="-transcription-state">
          {{ 'transcribe.inProgress' | culture }} <br />
          {{ 'transcribe.inProgressSubtitle' | culture }}
        </div>
      </template>

      <template v-if="showSegmentsList">
        <transcribe-segment
          v-for="(segment, i) in segments"
          :key="segment.guid"
          :ref="'segment' + segment.guid"
          :segment="segment"
          :transcript="transcriptions[segment.assetUUID]"
          :guid="segment.guid"
          :playbackTime="playbackTime"
          :isActive="activeSegmentGuid === segment.guid"
          :containerScrollTop="containerScrollTop"
          :isSingleSegment="hasSingleSegment"
          :isFirst="0 === i"
          @changeTime="changeTime"
          @pausePlayback="pausePlayback"
          @setActive="setActiveSegment(segment.guid)"
          @closeModal="closeModal"
          @saveTokens="saveSegmentTokens"
        />
      </template>
    </div>

    <div class="-bottom">
      <div class="-player-container">
        <transcribe-player
          v-if="activeSegment"
          :key="activeSegment.guid"
          ref="player"
          :src="mixinSrc(activeSegment.cdnUrl)"
          :timeLimit="activeSegment.timeLimit"
          @canPlay="onPlayerReady"
          @timeChanged="onPlayerTimeChange"
          class="-player"
        />
        <div class="-search-container" v-if="searchEnabled">
          <i
            class="wticon wticon-magnifier search-button"
            :class="{
              '-active': showSearch,
            }"
            @click="toggleSearch"
            title="Search Transcript"
            v-if="!showSearch"
          ></i>
          <div v-if="showSearch" class="search-input-container">
            <input
              ref="searchQuery"
              v-model="searchQuery"
              placeholder="Search transcript..."
              @keydown.stop="handleKeydownSearch"
              @keypress.enter="nextSearchResult(1)"
            />
            <div class="-search-controls">
              <i
                class="wticon wticon-chevron-up search-action"
                :class="{
                  '-disabled': !numberOfSearchResults,
                }"
                @click="nextSearchResult(-1)"
                :title="numberOfSearchResults ? 'Previous' : ''"
              ></i>
              <i
                class="wticon wticon-chevron-down search-action"
                :class="{
                  '-disabled': !numberOfSearchResults,
                }"
                @click="nextSearchResult(1)"
                :title="numberOfSearchResults ? 'Next' : ''"
              ></i>
              <i
                class="wticon wticon-cross3 search-action"
                @click="toggleSearch"
                title="Close"
              ></i>
            </div>
          </div>
        </div>
      </div>
      <div class="-actions">
        <div class="-left">
          <div
            v-if="showResetToOriginal"
            class="-reset wt2-link"
            :title="'transcribe.reset' | culture"
            @click="activeSegmentComponent.resetToOriginal()"
          >
            {{ 'transcribe.reset' | culture }}
          </div>

          <div v-if="showResetToOriginal" class="-sep-line" />
        </div>

        <wt-button
          type="primary"
          tabindex="0"
          :text="'transcribe.close' | culture"
          @click="close"
        />
      </div>
    </div>
  </div>
</template>

<script>
import TranscribePlayer from './components/TranscribePlayer.vue';
import TranscribeSegment from './components/TranscribeSegment.vue';
import { mapGetters, mapState, mapActions } from 'vuex';
import Analytics from 'c/WtAnalytics';
import app from '#/app';
import { workPool } from '@wochitnpm/helpers';

export default {
  name: 'wt-transcribe',
  components: {
    TranscribePlayer,
    TranscribeSegment,
  },

  props: {
    closeModal: { type: Function, required: false },
    list: { type: [Array], required: true },
    opener: {
      type: String,
      required: true,
      validator: (val) => Object.values(app.TRANSCRIPT_OPENER).includes(val),
    },
    playheadTime: { type: Number, required: false, default: 0 },
  },

  data() {
    return {
      title: '',
      cdnUrl: '',
      segments: [],
      activeSegmentGuid: null,
      playbackTime: null,
      containerScrollTop: 0,
      autoPlayOnSegmentChange: false,
      isUserOwnTranscript: false,
      failedToCreate: false,
      workPoolHandlers: new Set(),
      numberOfSearchResults: 0,
      searchQuery: '',
      showSearch: false,
    };
  },

  computed: {
    ...mapState({
      transcriptions: (state) => state.story.transcriptions,
      selectedAssets: (state) => state.story.selectedAssets,
    }),
    ...mapGetters([
      'selectedVoiceOverSegments',
      'isSomeTranscribing',
      'visualCandidatesForTranscribe',
    ]),
    ...mapGetters('timemachine', ['visualData']),

    searchEnabled() {
      const that = this.currentTranscript;
      return that && this.hasSingleSegment && !this.failedToCreate;
    },

    activeSegmentRef() {
      return this.$refs['segment' + this.activeSegmentGuid];
    },

    activeSegment() {
      return this.getSegmentsByGuid(this.activeSegmentGuid);
    },

    showFailedState() {
      return (
        this.isInLightbox &&
        this.currentTranscript?.status === 'FAILED' &&
        !this.failedToCreate
      );
    },

    showInProgressState() {
      return (
        this.isInLightbox &&
        /PENDING|PROCESSING/.test(this.currentTranscript?.status)
      );
    },

    showSegmentsList() {
      return (
        this.isUserOwnTranscript ||
        (!this.showFailedState &&
          !this.showInProgressState &&
          !this.failedToCreate)
      );
    },

    showResetToOriginal() {
      const that = this.currentTranscript;
      return (
        that &&
        this.isInLightbox &&
        !that.isOriginal &&
        /DONE|UPDATED/.test(that.status)
      );
    },

    showTranscribeVideo() {
      const that = this.currentTranscript;
      return (
        that &&
        app.transcribeSubType.VIDEO === that.transcribeSubType &&
        this.isInLightbox &&
        !that.isOriginal &&
        /FAILED/.test(that.status) &&
        !this.failedToCreate
      );
    },

    hasSingleSegment() {
      return this.list.length === 1;
    },

    hasMultipleSegments() {
      return this.list.length > 1;
    },

    currentTranscript() {
      this.$log(
        'activeSegment:',
        this.activeSegment,
        'currentTranscript:',
        this.transcriptions[this.activeSegment?.assetUUID]
      );
      return this.transcriptions[this.activeSegment?.assetUUID];
    },

    activeSegmentComponent() {
      return this.$refs['segment' + this.activeSegmentGuid][0];
    },

    isInLightbox() {
      return (
        app.TRANSCRIPT_OPENER.LIGHTBOX_MENU === this.opener ||
        app.TRANSCRIPT_OPENER.LIGHTBOX_PREVIEW === this.opener
      );
    },

    isInToolbar() {
      return app.TRANSCRIPT_OPENER.TOOLBAR === this.opener;
    },

    isInTimeline() {
      return (
        app.TRANSCRIPT_OPENER.TIMELINE_TEXT === this.opener ||
        app.TRANSCRIPT_OPENER.TIMELINE_VO === this.opener
      );
    },

    showSegmentTitle() {
      return this.isInLightbox;
    },

    showHeaderStatus() {
      return this.isSomeTranscribing && (this.isInToolbar || this.isInTimeline);
    },
  },

  beforeDestroy() {
    app.keyboardAccessibility.remove(app.KEYBOARD_LAYER.TRANSCRIBE);
    workPool.off(this.workPoolHandlers);
  },

  created() {
    Analytics.trigger('VO:openTranscriptModal', { using: this.opener });

    let firstGuid;
    let activeSegmentGuid;
    const segments = [];

    _.each(this.list, (t) => {
      const guid = app.guid(t.assetUUID);

      if (app.transcribeSubType.NARRATION === t.transcribeSubType) {
        const segment = _.find(this.selectedVoiceOverSegments, (segment) => {
          return (
            segment.audioSegmentContent.asset.metadata.assetUUID === t.assetUUID
          );
        })?.audioSegmentContent;

        if (segment) {
          segments.push({
            assetUUID: t.assetUUID,
            transcribeSubType: t.transcribeSubType,
            timeLimit: t.timeLimit,
            duration: segment.asset.metadata.duration,
            cdnUrl: segment.cdnUrl,
            title: segment.asset.metadata.title,
            guid,
          });
        }
      } else if (app.transcribeSubType.VIDEO === t.transcribeSubType) {
        let asset = _.find(
          this.selectedAssets,
          (lightboxAsset) => lightboxAsset.metadata.assetUUID === t.assetUUID
        );
        if (!asset) {
          asset = _.find(
            this.visualData,
            (timelineAsset) => timelineAsset.metadata.assetUUID === t.assetUUID
          );
        }

        if (asset) {
          segments.push({
            assetUUID: t.assetUUID,
            transcribeSubType: t.transcribeSubType,
            timeLimit: t.timeLimit,
            duration: asset.metadata.duration,
            cdnUrl: asset.metadata.urlCdn || asset.metadata.url,
            title: asset.metadata.title,
            guid,
          });
        }
      } else {
        app.doctorController.report(
          `unknown transcribeSubType: '${t.transcribeSubType}'`
        );
      }

      if (
        this.playheadTime &&
        _.between(
          t.timeLimit.displayStartTime,
          this.playheadTime,
          t.timeLimit.displayEndTime,
          { includeMax: false }
        )
      ) {
        activeSegmentGuid = guid;
      }
      if (!firstGuid) {
        firstGuid = guid;
      }
    });

    this.segments = segments;
    this.activeSegmentGuid = activeSegmentGuid || firstGuid;
  },

  mounted() {
    this.bindKeys();
    if (!this.isInLightbox && this.activeSegment) {
      const segment = this.activeSegment;
      const { displayStartTime, displayEndTime, startTime } = segment.timeLimit;
      if (
        this.playheadTime &&
        _.between(displayStartTime, this.playheadTime, displayEndTime)
      ) {
        const relativeTime = this.playheadTime - displayStartTime + startTime;
        this.changeTime(relativeTime);
      }
    }
    _.delay(() => {
      this.scrollIntoSegment(this.activeSegmentGuid);
    }, 100);
  },

  watch: {
    searchQuery() {
      this.handleSearch();
    },
  },

  methods: {
    ...mapActions(['SAVE_TRANSCRIPTION', 'FETCH_TRANSCRIPTION']),

    handleSearch() {
      if (!this.activeSegmentRef[0]) {
        return;
      }
      if (!this.searchQuery.length) {
        this.activeSegmentRef[0].search(null);
        this.numberOfSearchResults = 0;
        return;
      }
      this.numberOfSearchResults = this.activeSegmentRef[0].search(
        this.searchQuery
      );
    },

    nextSearchResult(direction) {
      if (this.activeSegmentRef[0]) {
        if (direction === 1) {
          this.activeSegmentRef[0].nextSearchResult();
        } else {
          this.activeSegmentRef[0].previousSearchResult();
        }
      }
    },

    toggleSearch() {
      this.showSearch = !this.showSearch;
      if (!this.showSearch) {
        this.searchQuery = '';
        this.numberOfSearchResults = 0;
      } else {
        this.$nextTick(() => {
          this.$refs.searchQuery.focus();
        });
      }
    },

    handleKeydownSearch(e) {
      e.stopImmediatePropagation();
      e.stopPropagation();
      if (e.key === 'Escape') {
        this.toggleSearch();
        e.target.blur(); // Add this line to blur the focus
      }
    },

    close() {
      this.closeModal();
    },

    saveSegmentTokens({ tokens, segmentGuid, isOriginal }) {
      const segment = this.getSegmentsByGuid(segmentGuid);
      const { assetUUID } = segment;
      this.SAVE_TRANSCRIPTION({ tokens, assetUUID, isOriginal }).catch((e) => {
        if (!this._isDestroyed) {
          this.forceUpdateSegmentsFromStore(assetUUID);
        }
      });
      this.forceUpdateSegmentsFromStore(assetUUID, segmentGuid); // keep after SAVE_TRANSCRIPTION
    },

    forceUpdateSegmentsFromStore(assetUUID, originGuid) {
      const allSegments = this.getSegmentsByAssetUUID(assetUUID);
      const originSegment = originGuid
        ? this.getSegmentsByGuid(originGuid)
        : null;

      _.each(allSegments, (tmpSegment, segmentIndex) => {
        const tmpGuid = tmpSegment.guid;
        const isOriginSegment = originGuid && tmpGuid === originGuid;

        if (!originSegment || !isOriginSegment) {
          // console.log(this.$refs['segment'+tmpGuid][0].$el, 'replaced');
          this.$refs['segment' + tmpGuid][0].forceUpdateSegmentFromStore();
        }
      });
    },

    scrollIntoSegment(segmentGuid) {
      if (!this._isDestroyed && this.$refs['segment' + segmentGuid]) {
        const $segmentEl = this.$refs['segment' + segmentGuid][0]?.$el;
        if ($segmentEl) {
          const containerScrollBottom =
            this.containerScrollTop +
            this.$refs.scrollArea.getBoundingClientRect().height;
          const segmentScrollTop = $segmentEl.offsetTop - 50;
          if (
            segmentScrollTop < this.containerScrollTop ||
            segmentScrollTop + $segmentEl.offsetHeight > containerScrollBottom
          ) {
            this.$refs.scrollArea.scrollTop = segmentScrollTop;
          }
        }
      }
    },

    onPlayerTimeChange(time) {
      this.playbackTime = time;
    },

    onPlayerReady() {
      if (this.autoPlayOnSegmentChange) {
        this.$refs.player.play();
      }
      this.autoPlayOnSegmentChange = false;
    },

    setActiveSegment(segmentGuid) {
      if (segmentGuid === this.activeSegmentGuid) {
        return;
      }
      // this.scrollIntoSegment(segmentGuid);
      this.autoPlayOnSegmentChange = !!this.$refs.player.isPlaying;
      this.activeSegmentGuid = segmentGuid;
    },

    changeTime(time) {
      this.$refs.player.changeTime(time, { isManual: true });
    },

    pausePlayback() {
      this.$refs.player.pause();
    },

    bindKeys() {
      const isMac = navigator.platform === 'MacIntel';
      const metaProp = isMac ? 'metaKey' : 'ctrlKey';
      const handlers = [
        {
          on: 'keydown',
          key: 90 /* Z*/,
          run: this.undo.bind(this),
          [metaProp]: true,
        },
        {
          on: 'keydown',
          key: 90 /* Z*/,
          run: this.redo.bind(this),
          [metaProp]: true,
          shiftKey: true,
        },
        { on: 'keydown', key: $.ui.keyCode.LEFT, run: _.noop },
        { on: 'keydown', key: $.ui.keyCode.RIGHT, run: _.noop },
        { on: 'keydown', key: 8, run: this.delete.bind(this) },
        { on: 'keyup', key: 187 /* plus*/, run: _.noop },
        { on: 'keyup', key: 189 /* minus*/, run: _.noop },
      ];
      if (!isMac) {
        handlers.push({
          on: 'keydown',
          key: 89 /* Y*/,
          run: this.redo.bind(this),
          ctrlKey: true,
        });
      }
      app.keyboardAccessibility.add(app.KEYBOARD_LAYER.TRANSCRIBE, handlers);
    },

    undo() {
      this.activeSegmentComponent.undo();
    },

    redo() {
      this.activeSegmentComponent.redo();
    },

    delete() {
      this.activeSegmentComponent.onClickDelete();
    },

    onScroll(e) {
      this.containerScrollTop = this.$refs.scrollArea.scrollTop;
    },

    getSegmentsByGuid(guid) {
      return this.segments.find((segment) => segment.guid === guid);
    },

    getSegmentsByAssetUUID(assetUUID) {
      return this.segments.filter((segment) => segment.assetUUID === assetUUID);
    },

    async writeYourOwn() {
      this.isUserOwnTranscript = true;
      await this.$nextTick();
      this.activeSegmentComponent?.onClickInsertYourOwn();
    },

    transcribeVideo() {
      const { assetUUID, transcribeSubType } = this.activeSegment;
      const asset = _.find(this.visualCandidatesForTranscribe, (a) => {
        return a.metadata.assetUUID === assetUUID;
      });

      this.FETCH_TRANSCRIPTION({
        asset,
        transcribeSubType,
        how: workPool.job.transcriptions.HOW.CREATE,
        alertOnError: true,
      });

      if (!this.workPoolHandlers.size) {
        workPool.on(
          {
            type: workPool.job.transcriptions.type,
            context: (jc) => jc.assetUUID === assetUUID,
          },
          (payload) => {
            this.failedToCreate = payload.failedToCreate;
          },
          this.workPoolHandlers
        );
      }
    },
  },
};
</script>

<style lang="scss">
/* outer modal configuration */
.wt-transcribe-modal {
  .wt2-modal-content {
    background: #fff;
    width: 800px !important;
    min-width: 800px !important;
  }
}
</style>

<style scoped lang="scss">
.wt-transcribe-modal-inner {
  position: relative;
  padding: 0;
  display: flex;
  flex-direction: column;
  min-height: 360px;
  max-height: calc(100vh - 15vh);

  .-top {
    overflow-y: overlay;
    flex-grow: 1;
  }

  .-bottom {
    display: flex;
    flex-direction: column;
    height: 100px;
    flex-shrink: 0;
    flex-grow: 0;
    .-player-container {
      display: flex;
      flex-direction: row;
      align-items: center;
      height: 50px;
      border-top: 1px solid #d9d9d9;
      padding: 0 33px;
    }
    .-player {
      flex-grow: 1;
    }
    .-search-container {
      margin-left: 20px;
    }

    .search-button {
      background: none;
      border: none;
      cursor: pointer;
      padding: 4px;
      color: inherit;

      &.-active,
      &:hover {
        color: #06a9f4;
      }

      &.-disabled {
        opacity: 0.5;
        cursor: not-allowed;
        pointer-events: none;
      }
    }
    .search-input-container {
      position: relative;

      input {
        width: 100%;
        padding: 4px 8px;
        border: 1px solid #ccc;
        border-radius: 4px;
        height: 32px;
        padding-right: 80px;
      }
    }
    .-search-controls {
      position: absolute;
      right: 0;
      top: 12px;
      right: 5px;
      font-size: 12px;
      transform: translateY(-50%);
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin-top: 5px;
      border-left: 1px solid #bababa;
      padding-left: 5px;
      .search-action {
        font-weight: 600;
        background: none;
        border: none;
        cursor: pointer;
        padding: 4px;
        color: inherit;
        padding-left: 5px;
        padding-right: 5px;
        &.-disabled {
          opacity: 0.5;
          pointer-events: none;
        }
        .wticon-chevron-up,
        .wticon-chevron-down {
          font-size: 14px;
        }
      }
    }
  }

  .close-icon {
    font-size: 18px;
    top: 15px !important;
    right: 15px !important;
    z-index: 2 !important;
  }

  .-header {
    display: flex;
    align-items: baseline;
    padding: 12px 45px 12px 20px;
    border-bottom: 1px solid #d9d9d9;
    margin-bottom: 0;
    width: 100%;
    background: #fff;

    .-title {
      flex-shrink: 0;
      margin: 0;
    }

    .-sep-line {
      display: inline-block;
      margin: auto 15px;
      height: 21px;
      width: 1px;
      background-color: #d9d9d9;
    }

    .-rec-name {
      flex-grow: 1;
      display: inline-block;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
      margin-top: 5px;
    }

    .-spring {
      width: 100%;
    }

    .-status {
      flex-shrink: 0;
      text-align: right;
      margin-left: 10px;
      padding-right: 26px;
      font-style: italic;
      color: $color-grey-2;
    }
  }

  .-transcription-state {
    display: flex;
    flex-grow: 1;
    align-items: center;
    justify-content: center;
    height: 210px;
    font-size: 14px;
    flex-direction: column;
    text-align: center;

    .wt-button {
      font-size: 16px;
      padding: 15px 20px;
      margin-top: 30px;
      cursor: pointer;
    }
  }

  .-actions {
    border-top: 1px solid #d9d9d9;
    background: #fff;
    justify-content: space-between;
    .-left {
      display: flex;
      align-items: center;
      padding-left: 20px;
    }
  }
}
</style>
